import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SEOHead } from '../components/seo';
import { absoluteSiteUrl } from '../lib/siteConfig';
import { HERO_OG_PATH } from '../lib/heroImage';
import { OrganizationSchema } from '../components/seo/OrganizationSchema';
import SkipLinks from '../components/accessibility/SkipLinks';
import HeroSection from '../components/homepage/HeroSection';
import QuickStartGuide from '../components/homepage/QuickStartGuide';
import WhyChooseSection from '../components/homepage/WhyChooseSection';
import HowItWorksSection from '../components/homepage/HowItWorksSection';
import FeaturedPropertiesSection from '../components/homepage/FeaturedPropertiesSection';
import PopularLocationsSection from '../components/homepage/PopularLocationsSection';
import ScrollToTopButton from '../components/homepage/ScrollToTopButton';
import { DeferMountUntilVisible } from '../components/homepage/DeferMountUntilVisible';
import { HomepageErrorBoundary, withHomepageErrorBoundary } from '../components/error';

// Create error boundary wrapped components
const SafeHeroSection = withHomepageErrorBoundary(HeroSection, 'Hero Section');
const SafeQuickStartGuide = withHomepageErrorBoundary(QuickStartGuide, 'Quick Start Guide');
const SafeWhyChooseSection = withHomepageErrorBoundary(WhyChooseSection, 'Why Choose Section');
const SafeHowItWorksSection = withHomepageErrorBoundary(HowItWorksSection, 'How It Works Section');
const SafeFeaturedPropertiesSection = withHomepageErrorBoundary(FeaturedPropertiesSection, 'Featured Properties');
const SafePopularLocationsSection = withHomepageErrorBoundary(PopularLocationsSection, 'Popular Locations');
const SafeScrollToTopButton = withHomepageErrorBoundary(ScrollToTopButton, 'Scroll to Top Button');

export default function HomePage() {
  const { hash } = useLocation();
  const mountFeaturedImmediately = hash === '#featured-properties';
  const mountPopularImmediately = hash === '#popular-locations';

  // Scroll to hash (e.g. #how-it-works) when navigating from footer or other in-app links
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      const frameId = requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, []);

  return (
    <HomepageErrorBoundary>
      <div id="top">
        <SkipLinks />
        <SEOHead 
          title="Premium Philippine Real Estate - Find Your Dream Home"
          description="Discover premium real estate properties in the Philippines. Find your dream home, rental property, or investment opportunity with RentaBahay. Expert agents, verified listings in Manila, Cebu, Davao, Makati & BGC."
          keywords="Philippine real estate, property rentals Philippines, rent apartment Manila, Cebu rental, Davao properties, BGC Makati condo, Quezon City house for rent, real estate agents Philippines, bahay for rent"
          image={absoluteSiteUrl(HERO_OG_PATH)}
          url={absoluteSiteUrl('/')}
          canonicalUrl={absoluteSiteUrl('/')}
          type="website"
        />
        <OrganizationSchema />
        
        {/* Hero Section */}
        <SafeHeroSection />

        {/* Main Content Area */}
        <div 
          id="main-content"
          className="focus:outline-none"
          tabIndex={-1}
          role="region"
          aria-label="Main content"
        >
          {/* Quick Start Guide */}
          <SafeQuickStartGuide />

          {/* Why Choose RentaBahay - Unified Section */}
          <SafeWhyChooseSection />

          {/* How It Works Section */}
          <SafeHowItWorksSection />
          
          {/* Featured Properties — deferred until near viewport to limit initial DOM & work */}
          <div id="featured-properties">
            <DeferMountUntilVisible
              force={mountFeaturedImmediately}
              minHeightClassName="min-h-[28rem]"
            >
              <SafeFeaturedPropertiesSection />
            </DeferMountUntilVisible>
          </div>

          {/* Popular Locations */}
          <div id="popular-locations">
            <DeferMountUntilVisible
              force={mountPopularImmediately}
              minHeightClassName="min-h-[22rem]"
            >
              <SafePopularLocationsSection />
            </DeferMountUntilVisible>
          </div>
        </div>
        
        {/* Floating Scroll to Top Button */}
        <SafeScrollToTopButton />
      </div>
    </HomepageErrorBoundary>
  );
}